import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
} from "@mui/material";
import { Search, Refresh } from "@mui/icons-material";
import moment from "moment";

import HeaderHelpPage from "../../../components/HeaderHelpPage";
import ButtonCustom from "../../../components/ButtonCustom";
import ExceptionHandlingDialog from "../../../components/finance-enhancement/ExceptionHandlingDialog";
import CircularLoader from "../../../components/circularLoader";
import CustomDatePicker from "../../../components/form/CustomDatePicker";
import { styles } from "../../../utils/styles";
import { sendDataWithPost } from "../../../services/services";
import { showErrorMsg, showSuccessMsg } from "../../../utils/toast";
import { APP_USER_SESSION } from "../../../constant/AppConstants";
import * as StorageUtils from "../../../utils/storageUtils";
import { EXCEPTION_ACTION_OPTIONS } from '../../../utils/constants';
import ShowDetail from '../../../components/ShowDetail';

const ExceptionHandlingPage = () => {
  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION).role;
  const exceptionHandlingRole = userRole.exception_handling || [];

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [source, setSource] = useState("");
  const [action, setAction] = useState("");
  const [acqTxnId, setAcqTxnId] = useState("");
  const [gwTxnId, setGwTxnId] = useState("");

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  const [dialogAction, setDialogAction] = useState("No Further Action");
  const [dialogRemarks, setDialogRemarks] = useState("");
  const [dialogSubmitting, setDialogSubmitting] = useState(false);

  const mapRow = (item, index) => ({
    id: item.id ?? index,
    acquirer_transaction_id: item.acquirer_transaction_id || "",
    gateway_transaction_id: item.gateway_transaction_id || "",
    transaction_datetime: item.transaction_datetime || "",
    source: item.source || "",
    action: item.action || "No Further Action",
    remarks: item.remarks || "",
  });

  const loadData = async (pageOverride, rowsPerPageOverride) => {
    setLoading(true);

    const currentPage = pageOverride ?? page;
    const currentRowsPerPage = rowsPerPageOverride ?? rowsPerPage;

    const body = {
      transaction_datetime_from:
        fromDate && moment(fromDate).isValid()
          ? moment(fromDate).format("YYYY-MM-DD HH:mm:ss")
          : null,
      transaction_datetime_to:
        toDate && moment(toDate).isValid()
          ? moment(toDate).format("YYYY-MM-DD HH:mm:ss")
          : null,
      source: source || null,
      action: action || null,
      acquirer_transaction_id: acqTxnId.trim() || null,
      gateway_transaction_id: gwTxnId.trim() || null,
      page: currentPage + 1,
      page_size: currentRowsPerPage,
    };

    try {
      const response = await sendDataWithPost(
        "/payout/ingestor/exception_list/",
        body
      );
      const data = response.data || {};
      const results = data.results || [];
      const mapped = results.map(mapRow);
      setRows(mapped);
      setCount(data.count ?? mapped.length);
      setPage(currentPage);
      setRowsPerPage(currentRowsPerPage);
    } catch (error) {
      console.error("Failed to load exception handling data:", error);
      showErrorMsg(
        error.response?.data?.detail || "Failed to load exception handling data"
      );
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    setPage(0);
    loadData(0, rowsPerPage);
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSource("");
    setAction("");
    setAcqTxnId("");
    setGwTxnId("");
    setPage(0);
    loadData(0, rowsPerPage);
  };

  const handleChangePage = (event, newPage) => {
    loadData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadData(0, newRowsPerPage);
  };
  const handleOpenEditDialog = (row) => {
    setSelectedRowForEdit(row);
    setDialogAction(row.action || "No Further Action");
    setDialogRemarks(row.remarks || "");
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedRowForEdit(null);
    setDialogAction("No Further Action");
    setDialogRemarks("");
  };

  const handleConfirmEdit = async () => {
    if (!selectedRowForEdit) return;
    const newAction = (dialogAction || "").trim();
    const newRemarks = (dialogRemarks || "").trim();

    if (!newAction) {
      showErrorMsg("Action is required");
      return;
    }
    if (!newRemarks) {
      showErrorMsg("Remarks is required");
      return;
    }

    try {
      setDialogSubmitting(true);
      await sendDataWithPost("/payout/ingestor/exception_update/", {
        id: selectedRowForEdit.id,
        action: newAction,
        remarks: newRemarks,
      });
      showSuccessMsg("Updated successfully");

      const updatedRows = rows.map((r) =>
        r.id === selectedRowForEdit.id
          ? { ...r, action: newAction, remarks: newRemarks }
          : r
      );
      setRows(updatedRows);
      handleCloseEditDialog();
    } catch (error) {
      console.error("Failed to update exception record:", error);
      showErrorMsg(
        error.response?.data?.detail || "Failed to update exception record"
      );
    } finally {
      setDialogSubmitting(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    return moment(value).format("DD/MM/YYYY HH:mm:ss");
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Exception Handling" isShowLink={false} />

      <Card
        sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: "1px solid #f0f0f0" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography
                sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }}
                variant="subtitle1"
              >
                Start Date
              </Typography>
              <CustomDatePicker
                value={fromDate}
                onChange={(val) => setFromDate(val)}
                fullWidth
                size="small"
                views={["year", "month", "day"]}
                openTo="day"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "30px",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography
                sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }}
                variant="subtitle1"
              >
                End Date
              </Typography>
              <CustomDatePicker
                value={toDate}
                onChange={(val) => setToDate(val)}
                fullWidth
                size="small"
                views={["year", "month", "day"]}
                openTo="day"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "30px",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography
                sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }}
                variant="subtitle1"
              >
                Source
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <MenuItem value="">
                    <Box>All</Box>
                  </MenuItem>
                  <MenuItem value="Incoming Exception">Incoming Exception</MenuItem>
                  <MenuItem value="Pending - No Fund">Pending - No Fund</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography
                sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }}
                variant="subtitle1"
              >
                Action
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <MenuItem value="">
                    <Box>All</Box>
                  </MenuItem>
                  {EXCEPTION_ACTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box>
              <Typography
                sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }}
                variant="subtitle1"
              >
                Acq Txn Id
              </Typography>
              <TextField
                value={acqTxnId}
                onChange={(e) => setAcqTxnId(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    height: "14px",
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography
                sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }}
                variant="subtitle1"
              >
                Gateway Txn Id
              </Typography>
              <TextField
                value={gwTxnId}
                onChange={(e) => setGwTxnId(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    height: "14px",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <ButtonCustom
            sx={{ ml: 1, mt: 2, ...styles.customButton }}
            icon={<Search />}
            variant="contained"
            title="Search"
            onClick={handleSearchClick}
          />
          <ButtonCustom
            sx={{ ml: 1, mt: 2 }}
            icon={<Refresh />}
            title="Reset"
            onClick={handleReset}
          />
        </Stack>
      </Card>

      {loading ? (
        <CircularLoader />
      ) : (
        <Card
          sx={{ borderRadius: 2, border: "1px solid #f0f0f0", position: "relative" }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Acq Txn ID
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      GW Txn ID
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Transaction Time
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Source
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Action
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Remarks
                    </Typography>
                  </TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography
                        gutterBottom
                        align="center"
                        variant="subtitle1"
                      >
                        Not found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover={exceptionHandlingRole.includes("Edit")}
                      sx={{ cursor: exceptionHandlingRole.includes("Edit") ? "pointer" : "default" }}
                      onClick={exceptionHandlingRole.includes("Edit") ? () => handleOpenEditDialog(row) : undefined}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {row.acquirer_transaction_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.gateway_transaction_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(row.transaction_datetime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.source}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.action}</Typography>
                      </TableCell>
                      <TableCell>
                        <ShowDetail maxWidth={300} text={row.remarks} limit={50} />
                      </TableCell>
                      <TableCell align="center" />
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      <ExceptionHandlingDialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        onSubmit={handleConfirmEdit}
        loading={dialogSubmitting}
        title="Edit Exception Handling"
        remarks={dialogRemarks}
        setRemarks={setDialogRemarks}
        action={dialogAction}
        setAction={setDialogAction}
      />
    </Container>
  );
};

export default ExceptionHandlingPage;
